import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Pill } from "@/components/perf";
import TournamentSchedule from "@/components/TournamentSchedule";
import PartnerBoard from "@/components/PartnerBoard";
import { toast } from "sonner";
import { Search, Plus, Zap, ClipboardCheck, MapPin, Trophy, ListChecks, ChevronDown } from "lucide-react";
import { TN_CSS } from "@/lib/tnButtons";
import TournamentPlacard from "@/components/TournamentPlacard";
import PsiReceipts from "@/components/PsiReceipts";
import { usePlayerCard } from "@/context/PlayerCardContext";

const input = "w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red";
const lbl = "block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5";
const EMPTY_EXT = { name: "", date: "", end_date: "", location: "", state: "", notes: "" };

export default function PlayerTournamentHub() {
  const { openPlayerCard } = usePlayerCard();
  const [hub, setHub] = useState(null);
  const [filters, setFilters] = useState({ name: "", date: "", state: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [ext, setExt] = useState(EMPTY_EXT);
  const [scoreFor, setScoreFor] = useState("");
  const [scoreAgainst, setScoreAgainst] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/player/tournaments/hub");
    setHub(data);
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  if (!hub) return <div className="max-w-6xl mx-auto px-6 py-16 text-zinc-500 animate-pulse">Loading your tournament hub…</div>;

  const allEvents = [...hub.registered.map((t) => ({ ...t, external: false })), ...hub.external];
  const matchesFilter = (ev) => {
    if (filters.name && !ev.name.toLowerCase().includes(filters.name.toLowerCase())) return false;
    if (filters.date) {
      const end = ev.end_date || ev.date;
      if (!(ev.date <= filters.date && filters.date <= end)) return false;
    }
    if (filters.state) {
      const hay = `${ev.state || ""} ${ev.location || ""}`.toLowerCase();
      if (!hay.includes(filters.state.toLowerCase())) return false;
    }
    return true;
  };
  const filtered = allEvents.filter(matchesFilter);
  const filtering = filters.name || filters.date || filters.state;
  const today = new Date().toISOString().slice(0, 10);
  const myUpcoming = hub.registered
    .filter((t) => t.status !== "complete" && (t.status === "in_progress" || (t.end_date || t.date || "") >= today))
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  const addExternal = async () => {
    if (!ext.name.trim() || !ext.date) return toast.error("Tournament name and start date are required");
    setBusy(true);
    try {
      await api.post("/player/tournaments/external", ext);
      toast.success("Tournament added to your schedule");
      setExt(EMPTY_EXT); setShowAdd(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const deleteExternal = async (id) => {
    await api.delete(`/player/tournaments/external/${id}`);
    load();
  };

  const submitScore = async () => {
    if (scoreFor === "" || scoreAgainst === "") return toast.error("Enter both scores");
    if (!window.confirm("Are you sure you want to submit this score for your most recent active match?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tournaments/${hub.game.tournament_id}/matches/${hub.game.match_key}/submit-score`,
        { score_for: Number(scoreFor), score_against: Number(scoreAgainst) });
      if (data.auto_approved) toast.success(`Score ${data.score} verified by player consensus — it's official! Check your next court assignment.`);
      else toast.success("Score submitted — it goes official once your opponents confirm the same score (or the TD approves)");
      setScoreFor(""); setScoreAgainst("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const { active, game, court, announcements } = hub;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="player-tournament-hub">
      <style>{TN_CSS}</style>
      <PageHeader ck="page.playerhub" kicker="Compete · Schedule · Live Match Flow"
        title="Players Tournament Hub"
        subtitle="Your tournament schedule, live match reporting, and court assignments — all in one place." />

      <div className="bg-pb-surface border border-pb-border p-4 mb-6" data-testid="hub-filter-bar">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-pb-red font-bold mb-3">
          <Search className="w-3.5 h-3.5" /> Filter Your Tournaments
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <input data-testid="filter-name" value={filters.name} onChange={(e) => setFilters({ ...filters, name: e.target.value })} placeholder="Tournament name…" className={input} />
          <input data-testid="filter-date" type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })} className={input} />
          <input data-testid="filter-state" value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} placeholder="State (e.g. FL)…" className={input} />
        </div>
        {filtering && (
          <div className="mt-3 space-y-1.5" data-testid="filter-results">
            {filtered.length === 0 && <div className="text-sm text-zinc-600">No tournaments match your filters.</div>}
            {filtered.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-3 text-sm border border-pb-border/60 px-3 py-2">
                <Trophy className={`w-3.5 h-3.5 ${t.external ? "text-pb-cyan" : "text-pb-red"}`} />
                <span className="font-semibold">{t.name}</span>
                <span className="text-zinc-500 text-xs">{t.date}{t.end_date ? ` – ${t.end_date}` : ""}</span>
                {(t.location || t.state) && <span className="text-zinc-500 text-xs flex items-center gap-1"><MapPin className="w-3 h-3" />{t.location}{t.state ? `, ${t.state}` : ""}</span>}
                <Pill color={t.external ? "#22D3EE" : "#A3E635"}>{t.external ? "My Entry" : "Bullpen"}</Pill>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-pb-surface border border-pb-border p-5 mb-6" data-testid="hub-schedule-box">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-display font-black uppercase text-xl">My Schedule</h3>
          <button data-testid="add-tournament-btn" onClick={() => setShowAdd(!showAdd)}
            className="tn-btn tn-create h-[52px] w-[210px] active:scale-95 transition-transform">
            <span className="tn-inner text-[10px] tracking-[0.18em]"><Plus className="w-4 h-4" /> {showAdd ? "Hide" : "Add Tournament"}</span>
          </button>
        </div>
        {showAdd && (
          <div className="border border-pb-cyan/40 bg-pb-cyan/5 p-4 mb-4 animate-fade-up" data-testid="add-tournament-form">
            <div className="text-xs text-zinc-400 mb-3">Add any tournament you're playing anywhere in the world — it'll show up in your schedule.</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2"><label className={lbl}>Tournament Name *</label>
                <input data-testid="ext-name" value={ext.name} onChange={(e) => setExt({ ...ext, name: e.target.value })} placeholder="US Open Pickleball Championships" className={input} /></div>
              <div><label className={lbl}>State</label>
                <input data-testid="ext-state" value={ext.state} onChange={(e) => setExt({ ...ext, state: e.target.value })} placeholder="FL" className={input} /></div>
              <div><label className={lbl}>Start Date *</label>
                <input data-testid="ext-date" type="date" value={ext.date} onChange={(e) => setExt({ ...ext, date: e.target.value })} className={input} /></div>
              <div><label className={lbl}>End Date</label>
                <input data-testid="ext-end-date" type="date" value={ext.end_date} onChange={(e) => setExt({ ...ext, end_date: e.target.value })} className={input} /></div>
              <div><label className={lbl}>City / Venue</label>
                <input data-testid="ext-location" value={ext.location} onChange={(e) => setExt({ ...ext, location: e.target.value })} placeholder="Naples" className={input} /></div>
              <div className="sm:col-span-3"><label className={lbl}>Notes</label>
                <input data-testid="ext-notes" value={ext.notes} onChange={(e) => setExt({ ...ext, notes: e.target.value })} placeholder="Partner, division, goals…" className={input} /></div>
            </div>
            <button data-testid="ext-submit" onClick={addExternal} disabled={busy}
              className="tn-btn tn-reg mt-3 h-[48px] w-[220px] active:scale-95 transition-transform disabled:opacity-50">
              <span className="tn-inner text-[10px] tracking-[0.18em]">{busy ? "Adding…" : "Add to Schedule"}</span>
            </button>
          </div>
        )}
        <TournamentSchedule events={filtered} onDelete={deleteExternal} />
      </div>

      <div className="bg-pb-surface border border-pb-border p-5 mb-6" data-testid="my-registered-events-box">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-pb-cyan font-bold mb-3">
          <ClipboardCheck className="w-3.5 h-3.5" /> Upcoming Registered Events
        </div>
        {myUpcoming.length === 0 ? (
          <div className="text-sm text-zinc-600" data-testid="no-registered-events">You're not registered for any upcoming events yet — browse the Tournament Hub and lock in your spot.</div>
        ) : (
          <div className="space-y-2.5">
            {myUpcoming.map((t) => (
              <a key={t.id} href={`/tournaments/${t.id}`} data-testid={`registered-event-${t.id}`}
                className="block border border-pb-border bg-pb-elevated/40 p-3 hover:border-pb-cyan/60 transition-colors">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display font-black uppercase text-sm text-white">{t.name}</span>
                  {t.status === "in_progress" && <span className="bg-green-500 text-black text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5">Live</span>}
                  <span className="ml-auto text-[11px] text-zinc-500">{t.date}{t.end_date && t.end_date !== t.date ? ` → ${t.end_date}` : ""}</span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {t.location && <span className="flex items-center gap-1 text-[10px] text-zinc-500 mr-1"><MapPin className="w-3 h-3" />{t.location}</span>}
                  {(t.my_entries || []).map((e, i) => (
                    <span key={i} className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest border ${e.pending ? "border-amber-500/60 text-amber-400" : "border-pb-cyan/50 text-pb-cyan"}`}>
                      {[e.event_type, e.division, e.age_bracket].filter(Boolean).join(" · ") || "Entered"}{e.pending ? " · pending" : ""}
                    </span>
                  ))}
                </div>
              </a>
            ))}
          </div>
        )}
      </div>

      <PartnerBoard />

      <div className="grid lg:grid-cols-3 gap-6">
        {active ? (
          <TournamentPlacard active={active} game={game} announcements={announcements}
            scoreFor={scoreFor} setScoreFor={setScoreFor} scoreAgainst={scoreAgainst} setScoreAgainst={setScoreAgainst}
            submitScore={submitScore} busy={busy} />
        ) : (
          <>
            <div className="bg-pb-surface border border-pb-border p-5" data-testid="active-tournament-box">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-pb-red font-bold mb-3">
                <Zap className="w-3.5 h-3.5" /> Active Tournament
              </div>
              <div className="text-sm text-zinc-600" data-testid="no-active-tournament">No live tournament right now. This box lights up automatically the moment one of your tournaments begins.</div>
            </div>

            <div className="bg-pb-surface border border-pb-border p-5" data-testid="game-results-box">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-pb-red font-bold mb-3">
                <ClipboardCheck className="w-3.5 h-3.5" /> Game Results
              </div>
              <div className="text-sm text-zinc-600" data-testid="no-game">No active match to report. When you finish a match in a live tournament, enter your team's score here.</div>
              {announcements?.length > 0 && (
                <div className="mt-4 space-y-1.5" data-testid="result-announcements">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-600">Approved Results</div>
                  {announcements.map((a, i) => (
                    <div key={i} className={`text-xs px-2.5 py-1.5 border ${a.won ? "border-pb-red/40 bg-pb-red/5 text-pb-red" : "border-pb-border text-zinc-400"}`}>
                      {a.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div data-testid="court-assignment-box"
          className={`border p-5 ${court?.status === "active" ? "border-green-500 bg-green-500/10" : court ? "border-amber-500/60 bg-amber-500/5" : "border-pb-border bg-pb-surface"}`}>
          <div className={`flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold mb-3 ${court?.status === "active" ? "text-green-400" : court ? "text-amber-400" : "text-pb-red"}`}>
            <MapPin className="w-3.5 h-3.5" /> Court Assignment
          </div>
          {court ? (
            court.status === "active" ? (
              <div className="space-y-2" data-testid="court-active">
                <div className="font-display font-black text-2xl uppercase text-green-400 leading-none">Active Next Match</div>
                <div className="text-sm text-zinc-200">{court.round_label}{court.event_type ? ` · ${court.event_type}` : ""}</div>
                <div className="text-sm">vs <span className="font-semibold">{court.opponent}</span></div>
                <div className="inline-block bg-green-500 text-black font-black font-display text-xl uppercase px-4 py-1.5" data-testid="court-number">Court {court.court}</div>
                <div className="text-xs text-green-300/80">Your court is ready — head over and start when both teams arrive.</div>
              </div>
            ) : (
              <div className="space-y-2" data-testid="court-pending">
                <div className="font-display font-black text-2xl uppercase text-amber-400 leading-none">Next Pending Match</div>
                <div className="text-sm text-zinc-200">{court.round_label}{court.event_type ? ` · ${court.event_type}` : ""}{court.age_bracket ? ` · ${court.age_bracket}` : ""}</div>
                <div className="text-sm">vs <span className="font-semibold">{court.opponent}</span></div>
                {court.forecast_court ? (
                  <>
                    <div className="inline-block bg-amber-500/20 border border-amber-500 text-amber-300 font-black font-display text-lg uppercase px-4 py-1.5" data-testid="court-forecast">On Deck — Court {court.forecast_court} expected next</div>
                    <div className="text-xs text-amber-300/80">A score just came in on that court — start warming up. It flips green the moment the TD approves.</div>
                  </>
                ) : (
                  <div className="text-xs text-amber-300/80">Waiting for a court to open up — you'll be assigned automatically to keep the tournament flowing.</div>
                )}
              </div>
            )
          ) : (
            <div className="text-sm text-zinc-600" data-testid="no-court">Court assignments appear here during live tournaments — after your score is approved, your next match and court show up automatically.</div>
          )}
        </div>
      </div>

      {active?.standings?.length > 0 && (
        <div className="bg-pb-surface border border-pb-border p-5 mt-6" data-testid="hub-standings-box">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-pb-red font-bold mb-3">
            <Trophy className="w-3.5 h-3.5" /> My Standings — {active.name}
          </div>
          {(active.playoffs || []).map((p) => (
            <div key={p.label} data-testid={`playoff-alert-${p.label}`}
              className={`mb-3 border px-3 py-2.5 ${p.made_playoffs ? "border-yellow-500/60 bg-yellow-500/10" : "border-pb-border bg-pb-elevated/40"}`}>
              {p.made_playoffs ? (
                <>
                  <div className="text-yellow-400 font-black uppercase tracking-widest text-[11px] mb-1.5">🏆 You made the playoffs — {p.label}!</div>
                  <div className="space-y-1">
                    {p.matches.map((pm, i) => (
                      <div key={i} className="text-xs text-zinc-300" data-testid={`playoff-match-${p.label}-${i}`}>
                        <span className="text-zinc-500 uppercase tracking-widest text-[9px] mr-1.5">{pm.stake}</span>
                        vs {pm.opponent}:{" "}
                        {pm.status === "complete" ? (
                          pm.won
                            ? <span className="text-green-400 font-bold">YOU WON {pm.score} ✓</span>
                            : <span className="text-red-400 font-bold">YOU LOST {pm.score}</span>
                        ) : <span className="text-pb-cyan font-bold">up next — watch for your court call</span>}
                      </div>
                    ))}
                    {p.matches.length === 0 && <div className="text-xs text-zinc-400">Your medal-round match is being set — stay close to the courts.</div>}
                  </div>
                </>
              ) : (
                <div className="text-xs text-zinc-500">Medal rounds are underway in {p.label} — you didn't advance this time. Great effort out there!</div>
              )}
            </div>
          ))}
          <div className="grid md:grid-cols-2 gap-6">
            {active.standings.map((s) => (
              <div key={s.label + (s.pool || "")} data-testid={`standings-${s.label}`}>
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">
                  {s.label}{s.pool ? ` · Pool ${s.pool}` : ""}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[9px] uppercase tracking-[0.2em] text-zinc-600 text-left">
                      <th className="pb-1.5 w-8">#</th><th className="pb-1.5">Team</th>
                      <th className="pb-1.5 text-center w-10">W</th><th className="pb-1.5 text-center w-10">L</th>
                      <th className="pb-1.5 text-right w-14">+/−</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.rows.map((r) => (
                      <tr key={r.rank} data-testid={r.me ? "standings-my-row" : undefined}
                        className={`border-t border-pb-border/50 ${r.me ? "bg-pb-red/10 text-pb-red font-bold" : "text-zinc-300"}`}>
                        <td className="py-1.5">{r.rank}</td>
                        <td className="py-1.5">
                          {(r.players || []).length > 0 ? (
                            r.players.map((p, pi) => (
                              <span key={p.id}>
                                {pi > 0 && <span className="text-zinc-600"> / </span>}
                                <button data-testid={`standings-player-${p.id}`} onClick={() => openPlayerCard(p.id)}
                                  className={`hover:underline underline-offset-2 transition-colors ${r.me ? "hover:text-white" : "hover:text-pb-cyan"}`}
                                  title={`View ${p.name}'s player card`}>{p.name}</button>
                              </span>
                            ))
                          ) : r.team}
                          {r.me && <span className="ml-1.5 text-[8px] uppercase tracking-widest border border-pb-red/50 px-1 align-middle">You</span>}
                        </td>
                        <td className="py-1.5 text-center">{r.wins}</td>
                        <td className="py-1.5 text-center">{r.losses}</td>
                        <td className={`py-1.5 text-right ${r.diff > 0 ? "" : r.diff < 0 ? "text-red-400" : ""}`}>{r.diff > 0 ? "+" : ""}{r.diff}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {s.stakes && (
                  <div className="mt-2 text-xs text-amber-300 border border-amber-500/30 bg-amber-500/5 px-2.5 py-1.5"
                    data-testid={`standings-stakes-${s.label}`}>
                    {s.stakes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <FinalResults results={hub.results} />

      <PsiReceipts />

      <ResultsLog />
    </div>
  );
}

const ordinal = (n) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? "th" : { 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;

function FinalResults({ results }) {
  const [open, setOpen] = useState(false);
  if (!results?.length) return null;
  return (
    <div className="bg-pb-surface border border-pb-border mt-6" data-testid="hub-final-results">
      <button data-testid="hub-final-results-toggle" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 p-5 text-left hover:bg-pb-elevated/40 transition-colors">
        <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-pb-red font-bold">
          <Trophy className="w-3.5 h-3.5" /> Final Results — Your Tournaments
          <span className="text-zinc-500 tracking-widest">({results.length})</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
      <div className="grid md:grid-cols-2 gap-3 px-5 pb-5">
        {results.map((r, i) => (
          <div key={i} className="border border-pb-border/70 bg-pb-elevated/40 p-4" data-testid={`final-result-${i}`}>
            <div className="flex flex-wrap items-baseline gap-x-2 mb-2.5">
              <span className="font-display font-black uppercase text-lg leading-none">{r.tournament}</span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">{r.label}{r.date ? ` · ${r.date}` : ""}</span>
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2"><span className="w-14 text-[9px] uppercase tracking-widest font-bold text-amber-400">Champ</span><span className="font-semibold text-amber-300">{r.champion_name || "—"}</span></div>
              {r.runner_up_name && <div className="flex items-center gap-2"><span className="w-14 text-[9px] uppercase tracking-widest font-bold text-zinc-400">2nd</span><span className="text-zinc-200">{r.runner_up_name}</span></div>}
              {r.third_name && <div className="flex items-center gap-2"><span className="w-14 text-[9px] uppercase tracking-widest font-bold text-orange-400/80">3rd</span><span className="text-zinc-300">{r.third_name}</span></div>}
            </div>
            {r.my_rank && (
              <div className={`mt-2.5 inline-block text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 border ${
                r.my_rank === 1 ? "border-amber-400 text-amber-300 bg-amber-500/10" : "border-pb-red/50 text-pb-red bg-pb-red/5"}`}
                data-testid={`final-result-my-finish-${i}`}>
                You finished {ordinal(r.my_rank)}{r.total ? ` of ${r.total}` : ""}{r.my_rank === 1 ? " 🥇" : r.my_rank === 2 ? " 🥈" : r.my_rank === 3 ? " 🥉" : ""}
              </div>
            )}
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

function ResultsLog() {
  const [log, setLog] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get("/player/tournaments/results-log").then(({ data }) => setLog(data)).catch(() => setLog([]));
  }, []);
  return (
    <div className="bg-pb-surface border border-pb-border mt-6" data-testid="hub-results-log">
      <button data-testid="hub-results-log-toggle" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 p-5 text-left hover:bg-pb-elevated/40 transition-colors">
        <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-pb-red font-bold">
          <ListChecks className="w-3.5 h-3.5" /> Tournament Results Log
          {log?.length > 0 && <span className="text-zinc-500 tracking-widest">({log.length})</span>}
        </span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
      <div className="px-5 pb-5">
      <p className="text-xs text-zinc-500 mb-3">TD-approved results from every tournament you're registered in — newest first.</p>
      {log === null ? (
        <div className="text-sm text-zinc-600 animate-pulse">Loading results…</div>
      ) : log.length === 0 ? (
        <div className="text-sm text-zinc-600" data-testid="results-log-empty">
          No approved results yet — once a Tournament Director approves match scores in your tournaments, they'll land here.
        </div>
      ) : (
        <div className="max-h-72 overflow-y-auto divide-y divide-pb-border/50 border border-pb-border/60" data-testid="results-log-list">
          {log.map((r, i) => (
            <div key={i} className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm ${r.medal ? "bg-yellow-500/[0.06]" : ""}`} data-testid={`results-log-row-${i}`}>
              {r.medal && (
                <span className="text-[8px] font-black uppercase tracking-widest text-yellow-400 border border-yellow-500/50 px-1.5 py-0.5" data-testid={`results-log-medal-${i}`}>
                  {r.medal === "bronze" ? "🥉 Bronze Match" : "🏅 Medal Match"}
                </span>
              )}
              <span className="font-semibold text-white">{r.winner}{r.medal === "final" ? " 🥇" : r.medal === "bronze" ? " 🥉" : ""}</span>
              <span className="text-[10px] uppercase tracking-widest text-pb-red font-bold">def.</span>
              <span className="text-zinc-300">{r.loser}{r.medal === "final" ? " 🥈" : ""}</span>
              <span className="font-mono text-pb-cyan">{r.score}</span>
              <span className="text-xs text-zinc-500 ml-auto">
                {r.tournament} · {r.round}{(r.label || r.event_type) ? ` · ${r.label || r.event_type}` : ""}{r.decided_at ? ` · ${new Date(r.decided_at).toLocaleDateString()}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
      </div>
      )}
    </div>
  );
}
